import { Apps, Edit } from '@mui/icons-material';
import { Box, Chip, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useUpdatePlaylistItem } from '~/api/playlist-items';
import { Tooltip } from '~/components/Tooltip';
import { InfoTooltip } from '~/components/Tooltip/InfoTooltip';
import { useAppContext } from '~/contexts/app';
import { toSeconds } from '~/lib/datetime';
import { formatRuntime } from '~/lib/runtime';
import type { PlaylistDesign__PlaylistItem as PlaylistItem } from './index.generated';
import { DurationContainer } from './styles';

export const DurationCell = ({ item, readOnly }: { item: PlaylistItem; readOnly: boolean }) => {
  const { t } = useTranslation();
  const [update] = useUpdatePlaylistItem();
  const [editing, setEditing] = useState(false);
  const [disabled, setDisabled] = useState(readOnly);

  const duration = useMemo(() => formatRuntime(item.duration, 3), [item]);

  const canEdit = useMemo(
    () => !item.durationReadonly && !readOnly,
    [item.durationReadonly, readOnly],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (!canEdit) return;
      setEditing(() => true);
    },
    [canEdit],
  );

  useEffect(() => setEditing(false), [item.duration]);

  // The button unmounts while editing, so leaving the field by keyboard would drop focus to the
  // page. Leaving it by clicking away keeps focus wherever the user clicked.
  const buttonRef = useRef<HTMLElement>(null);
  const finishEditing = useCallback(() => {
    flushSync(() => setEditing(false));
    buttonRef.current?.focus();
  }, []);

  const handleKeypress = useCallback(
    async (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'Escape') finishEditing();
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
        // Focus may be back on the button before Enter's keypress, which would reopen the field.
        event.preventDefault();
        const value = event.target.value.trim();
        const seconds = parseInt(value);
        const oldSeconds = toSeconds(item.duration ?? {});
        if (seconds >= 5 && seconds <= 86400 && seconds !== oldSeconds) {
          setDisabled(true);
          await update({
            variables: { playlistItemId: item.id, patch: { duration: { seconds } } },
          });
          setDisabled(false);
        }
        finishEditing();
      }
    },
    [item, update, finishEditing],
  );

  if (!duration) return null;

  if (editing) {
    return (
      <TextField
        autoFocus
        disabled={disabled}
        defaultValue={item.duration ? toSeconds(item.duration) : ''}
        onBlur={() => setEditing(() => false)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeypress}
        sx={{ width: '100%' }}
        size="small"
        type="number"
      />
    );
  }

  return (
    <Tooltip
      describeChild
      title={
        canEdit
          ? t('components.playlist_reorder_list.duration_edit_tooltip')
          : t('components.playlist_reorder_list.duration_readonly_tooltip')
      }
    >
      <DurationContainer
        {...(canEdit ? { component: 'button', type: 'button' } : { className: 'readonly' })}
        onClick={handleClick}
        ref={buttonRef}
      >
        <span>{duration}</span>
        {canEdit && <Edit color="secondary" sx={{ fontSize: '14px' }} />}
      </DurationContainer>
    </Tooltip>
  );
};

const toDateTime = (sqlDate: string | null | undefined): DateTime | null =>
  sqlDate ? DateTime.fromSQL(sqlDate) : null;

interface DateCellProps {
  value: DateTime | null;
  onUpdate: (date: DateTime | null) => void;
  placeholder?: string;
  readOnly?: boolean;
  disablePast?: boolean;
}

const DateCell = ({
  value,
  onUpdate,
  placeholder = '--',
  readOnly = false,
  disablePast = false,
}: DateCellProps) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'background.default',
        borderRadius: '4px',
        color: 'text.primary',
        cursor: readOnly ? 'not-allowed' : 'pointer',
        display: 'flex',
        gap: 1,
        width: '100%',
      }}
    >
      <DatePicker
        desktopModeMediaQuery="@media (min-width: 0px)"
        disablePast={disablePast}
        disabled={readOnly}
        format="ccc, DD"
        onChange={(v) => {
          if (!readOnly) onUpdate(v);
        }}
        slotProps={{
          field: {
            clearable: !readOnly,
          },
          inputAdornment: {
            position: 'start',
            sx: {
              svg: {
                color: readOnly ? 'default' : 'primary.dark',
                fontSize: '20px',
              },
              marginRight: 1,
            },
          },
          textField: {
            inputProps: {
              disabled: true,
            },
            InputProps: {
              disableUnderline: true,
              sx: {
                fontSize: '14px',
                padding: '8px 12px',
                width: '100%',
              },
            },
            placeholder,
            variant: 'standard',
            fullWidth: true,
          },
        }}
        value={value}
      />
    </Box>
  );
};

export const StartsAtHeader = () => {
  const { t } = useTranslation();
  return (
    <>
      {t('components.playlist_reorder_list.starts_header')}
      <InfoTooltip title={t('components.playlist_reorder_list.starts_tooltip')} sx={{ ml: 1 }} />
    </>
  );
};

export const StartsAtCell = ({ item, readOnly }: { item: PlaylistItem; readOnly: boolean }) => {
  const [updatePlaylistItem] = useUpdatePlaylistItem();

  const update = useCallback(
    async (date: DateTime | null) => {
      await updatePlaylistItem({
        variables: { playlistItemId: item.id, patch: { startsAt: date?.toSQLDate() ?? null } },
      });
    },
    [item.id, updatePlaylistItem],
  );
  return (
    <DateCell
      value={toDateTime(item.startsAt)}
      onUpdate={update}
      placeholder="--"
      readOnly={readOnly}
      disablePast
    />
  );
};

export const EndsAtHeader = () => {
  const { t } = useTranslation();
  return (
    <>
      {t('components.playlist_reorder_list.ends_header')}
      <InfoTooltip title={t('components.playlist_reorder_list.ends_tooltip')} sx={{ ml: 1 }} />
    </>
  );
};

export const EndsAtCell = ({ item, readOnly }: { item: PlaylistItem; readOnly: boolean }) => {
  const [updatePlaylistItem] = useUpdatePlaylistItem();

  const update = useCallback(
    async (date: DateTime | null) => {
      await updatePlaylistItem({
        variables: { playlistItemId: item.id, patch: { endsAt: date?.toSQLDate() ?? null } },
      });
    },
    [item.id, updatePlaylistItem],
  );
  return (
    <DateCell
      value={toDateTime(item.endsAt)}
      onUpdate={update}
      placeholder="--"
      readOnly={readOnly}
      disablePast
    />
  );
};

export const ThumbnailCell = ({ item }: { item: PlaylistItem }) => (
  <Box
    sx={{
      height: '32px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      overflow: 'hidden',
      bgcolor: item.contentItem.thumbnailUri ? 'transparent' : 'background.default',
    }}
  >
    {item.contentItem.thumbnailUri ? (
      <img
        alt=""
        crossOrigin="anonymous"
        src={item.contentItem.thumbnailUri}
        style={{ height: '100%', width: '100%', objectFit: 'cover' }}
      />
    ) : (
      <Apps color="secondary" />
    )}
  </Box>
);

export const getPlaylistItemStatus = (
  item: PlaylistItem,
  today: DateTime,
  zone: string,
  t: (key: string) => string,
) => {
  const startDate = item.startsAt ? DateTime.fromSQL(item.startsAt, { zone }) : null;
  const endDate = item.endsAt ? DateTime.fromSQL(item.endsAt, { zone }) : null;

  return startDate && startDate.isValid && today < startDate
    ? { label: t('components.playlist_reorder_list.status_upcoming'), color: 'info' as const }
    : endDate && endDate.isValid && endDate < today
    ? { label: t('components.playlist_reorder_list.status_expired'), color: 'error' as const }
    : { label: t('common.status.active'), color: 'success' as const };
};

export const StatusCell = ({ item }: { item: PlaylistItem }) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const zone = currentNetwork.timeZone;
  const today = DateTime.now().setZone(zone).startOf('day');
  const status = getPlaylistItemStatus(item, today, zone, t);
  return <Chip label={status.label} color={status.color} size="small" />;
};
